import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Wallet, Plus, X, Pencil, Trash2, Check,
  Home, Zap, CreditCard, DollarSign, Receipt, GraduationCap, Building, Package,
  ChevronLeft, ChevronRight, Search, Repeat, Copy, Download, BarChart3, Target,
  ChevronDown, ChevronUp, Camera, Image as ImageIcon, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import ShareWhatsappButton from './ShareWhatsappButton';
import ExpenseChartsSection from './ExpenseChartsSection';
import { isOverdue } from '../utils/isOverdue';
import { expenseStatus, statusLabel, statusEmoji } from '../utils/expenseStatus';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { key: 'casa',      label: 'Casa',      icon: Home,           color: 'bg-blue-100 text-blue-800 dark:bg-blue-400/20 dark:text-blue-300 dark:ring-1 dark:ring-blue-400/40' },
  { key: 'factura',   label: 'Facturas',  icon: Zap,            color: 'bg-amber-100 text-amber-800 dark:bg-amber-400/20 dark:text-amber-300 dark:ring-1 dark:ring-amber-400/40' },
  { key: 'tarjeta',   label: 'Tarjeta',   icon: CreditCard,     color: 'bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-500/25 dark:text-fuchsia-300 dark:ring-1 dark:ring-fuchsia-400/40' },
  { key: 'deuda',     label: 'Deudas',    icon: DollarSign,     color: 'bg-red-100 text-red-700 dark:bg-red-500/25 dark:text-red-300 dark:ring-1 dark:ring-red-400/40' },
  { key: 'recibo',    label: 'Recibos',   icon: Receipt,        color: 'bg-sky-100 text-sky-800 dark:bg-sky-400/20 dark:text-sky-300 dark:ring-1 dark:ring-sky-400/40' },
  { key: 'colegio',   label: 'Colegio',   icon: GraduationCap,  color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-400/20 dark:text-emerald-300 dark:ring-1 dark:ring-emerald-400/40' },
  { key: 'expensas',  label: 'Expensas',  icon: Building,       color: 'bg-teal-100 text-teal-800 dark:bg-teal-400/20 dark:text-teal-300 dark:ring-1 dark:ring-teal-400/40' },
  { key: 'otros',     label: 'Otros',     icon: Package,        color: 'bg-slate-200 text-slate-700 dark:bg-slate-500/25 dark:text-slate-300 dark:ring-1 dark:ring-slate-400/30' },
];
const CATEGORY_MAP = Object.fromEntries(CATEGORIES.map((c) => [c.key, c]));

const MONTHS_ES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

const currentPeriod = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const formatMoney = (n, currency = 'ARS') =>
  `${currency} ${Number(n || 0).toLocaleString('es-AR', { maximumFractionDigits: 0 })}`;

// Variación por gasto recurrente (Feb 2026).
// Convierte un `variation` block del backend (`_compute_recurrent_variation`)
// en un chip visual con la iconografía obligatoria: 🔺 sube, 📉 baja, ➖ igual.
// Si moneda del anterior != actual → chip informativo sin %. Si no hay período
// anterior → devuelve null (no se pinta).
const MONTHS_AR = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

const periodToMonthName = (yyyymm) => {
  if (!yyyymm || typeof yyyymm !== 'string') return null;
  const parts = yyyymm.split('-');
  const y = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) return null;
  return `${MONTHS_AR[m - 1]}${new Date().getFullYear() !== y ? ` ${y}` : ''}`;
};

const VariationChip = ({ variation, testId }) => {
  if (!variation || !variation.has_prev) return null;
  const prevLabel = periodToMonthName(variation.previous_period);
  const prevCur = variation.previous_currency || variation.currency || 'ARS';
  if (variation.currency_mismatch) {
    return (
      <span
        data-testid={testId}
        className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-400/15 text-amber-800 dark:text-amber-200"
        title={`Anterior: ${formatMoney(variation.previous_amount, prevCur)}`}
      >
        ⚠️ moneda distinta vs {prevLabel || variation.previous_period}
      </span>
    );
  }
  const diff = Number(variation.diff || 0);
  const pct = variation.diff_pct;
  const absDiff = Math.abs(diff);
  let icon = '➖';
  let colorCls = 'bg-muted text-muted-foreground';
  let sign = '';
  if (diff > 0) {
    icon = '🔺';
    colorCls = 'bg-red-100 dark:bg-red-400/15 text-red-700 dark:text-red-300';
    sign = '+';
  } else if (diff < 0) {
    icon = '📉';
    colorCls = 'bg-emerald-100 dark:bg-emerald-400/15 text-emerald-700 dark:text-emerald-300';
    sign = '-';
  }
  const pctLabel = pct === null || pct === undefined
    ? null
    : `${sign}${Math.abs(pct).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
  const pctIcon = diff > 0 ? '💹' : (diff < 0 ? '📉' : '');
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${colorCls}`}
      title={`Anterior (${variation.previous_period}): ${formatMoney(variation.previous_amount, prevCur)}`}
    >
      <span>{icon} {sign}{formatMoney(absDiff, prevCur)}</span>
      {pctLabel && (
        <span className="opacity-80">· {pctIcon} {pctLabel}</span>
      )}
      {prevLabel && <span className="opacity-70">vs {prevLabel}</span>}
    </span>
  );
};

const emptyDraft = () => ({
  title: '', amount: '', currency: 'ARS', category: 'factura',
  period: currentPeriod(), due_date: '', paid: false, paid_by: '',
  shared_with: '', notes: '', recurrent: false,
  exchange_rate: '', // F3 multidivisa — sólo se envía cuando currency !== primary
});

// F3 — memoria local de la última moneda usada al crear un gasto.
const LAST_CURRENCY_KEY = 'vidaenorden.lastCurrency';
const readLastCurrency = () => {
  try { return window.localStorage.getItem(LAST_CURRENCY_KEY) || null; } catch { return null; }
};
const writeLastCurrency = (code) => {
  if (!code) return;
  try { window.localStorage.setItem(LAST_CURRENCY_KEY, code); } catch { /* noop */ }
};

const formatConverted = (amount, primary) => {
  const n = Number(amount || 0);
  if (!isFinite(n)) return '';
  return `${primary} ${n.toLocaleString('es-AR', { maximumFractionDigits: 0 })}`;
};

const shiftPeriod = (period, delta) => {
  const [y, m] = period.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const shiftDate = (dateStr, monthsDelta) => {
  // dateStr in YYYY-MM-DD → add N months preserving day (clamped to end of month)
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr || '');
  if (!m) return dateStr || '';
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1 + monthsDelta;
  const day = Number(m[3]);
  const target = new Date(y, mo, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(day, lastDay));
  return `${target.getFullYear()}-${String(target.getMonth() + 1).padStart(2, '0')}-${String(target.getDate()).padStart(2, '0')}`;
};

const periodLabel = (period) => {
  const [y, m] = period.split('-').map(Number);
  return `${MONTHS_ES[m - 1]} ${y}`;
};

export default function ExpensesView() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total: 0, paid: 0, pending: 0, currency: 'ARS' });
  const [period, setPeriod] = useState(currentPeriod());
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  // F3 — señalar al layout global que hay un formulario de gasto abierto
  // (Dashboard usa esta flag para ocultar el FAB flotante y evitar overlap).
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.body.dataset.expenseFormOpen = showForm ? '1' : '0';
    return () => { document.body.dataset.expenseFormOpen = '0'; };
  }, [showForm]);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(emptyDraft());
  const [saving, setSaving] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copySource, setCopySource] = useState([]); // items from previous month
  const [copyDraft, setCopyDraft] = useState([]); // {id, selected, title, amount, category, due_date, ...}
  const [copyLoading, setCopyLoading] = useState(false);
  const [copyBusy, setCopyBusy] = useState(false);
  const [showYearlyModal, setShowYearlyModal] = useState(false);
  const [yearlyData, setYearlyData] = useState(null);
  const [yearlyLoading, setYearlyLoading] = useState(false);
  const [yearlyYear, setYearlyYear] = useState(new Date().getFullYear());
  const [budgetStatus, setBudgetStatus] = useState([]);
  const [showBudgets, setShowBudgets] = useState(false);
  const [budgetDraft, setBudgetDraft] = useState({});
  const [budgetSavingCat, setBudgetSavingCat] = useState(null);
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null);
  const [photoAnalyzing, setPhotoAnalyzing] = useState(false);
  const [photoData, setPhotoData] = useState(null); // extracted from AI
  const [photoDraft, setPhotoDraft] = useState(null); // editable form derived from AI
  const [photoSaving, setPhotoSaving] = useState(false);
  const [viewPhotoUrl, setViewPhotoUrl] = useState(null);

  // F3 multidivisa — monedas habilitadas + moneda principal del usuario.
  const [currencyConfig, setCurrencyConfig] = useState({ primary: 'ARS', enabled: [{ code: 'ARS', name: 'Peso argentino', flag: '🇦🇷', symbol: '$' }] });
  useEffect(() => {
    axios.get(`${API}/api/currencies`, { withCredentials: true })
      .then((r) => setCurrencyConfig({
        primary: r.data.primary || 'ARS',
        enabled: r.data.enabled || [{ code: 'ARS', name: 'Peso argentino', flag: '', symbol: '$' }],
      }))
      .catch(() => { /* mantener default */ });
  }, []);
  const enabledMap = useMemo(() => {
    const m = {}; (currencyConfig.enabled || []).forEach((c) => { m[c.code] = c; }); return m;
  }, [currencyConfig.enabled]);
  const [photoBaseline, setPhotoBaseline] = useState(null); // {avg, sample_size, currency, category}
  const photoInputRef = React.useRef(null);

  const exportPdf = () => {
    const url = `${API}/api/expenses/export.pdf?period=${period}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  const exportCsv = () => {
    const url = `${API}/api/expenses/export.csv?period=${period}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const openPhotoFlow = () => {
    setPhotoFile(null);
    setPhotoPreviewUrl(null);
    setPhotoData(null);
    setPhotoDraft(null);
    setPhotoModalOpen(true);
    setTimeout(() => photoInputRef.current?.click(), 50);
  };
  const closePhotoFlow = () => {
    if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
    setPhotoFile(null);
    setPhotoPreviewUrl(null);
    setPhotoData(null);
    setPhotoDraft(null);
    setPhotoBaseline(null);
    setPhotoModalOpen(false);
  };

  const CAT_MAP_FROM_DOC = {
    factura: 'factura', recibo: 'recibo', comprobante: 'recibo',
    nota_escolar: 'colegio', presupuesto: 'otros', orden: 'otros',
  };

  const handlePhotoSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
    const url = URL.createObjectURL(file);
    setPhotoFile(file);
    setPhotoPreviewUrl(url);
    setPhotoAnalyzing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/api/documents/analyze`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPhotoData(data);
      // Prefill editable draft
      const due = data.due_date || '';
      const derivedPeriod = due
        ? due.slice(0, 7)
        : period;
      setPhotoDraft({
        title: data.service_name || 'Gasto',
        amount: String(data.amount ?? ''),
        currency: data.currency || 'ARS',
        category: CAT_MAP_FROM_DOC[data.document_type] || 'otros',
        period: derivedPeriod,
        due_date: due,
        paid_by: data.assigned_to || '',
        shared_with: '',
        notes: [data.notes, data.action_required, data.items_to_bring && `Llevar: ${data.items_to_bring}`]
          .filter(Boolean).join(' · '),
        paid: false,
      });
    } catch {
      toast.error('No pudimos analizar la foto. Podés cargar el gasto a mano.');
    } finally {
      setPhotoAnalyzing(false);
    }
  };

  const confirmPhotoExpense = async () => {
    if (!photoFile || !photoDraft) return;
    const amt = parseFloat(photoDraft.amount);
    if (isNaN(amt) || amt < 0) {
      toast.error('Ingresá un monto válido');
      return;
    }
    setPhotoSaving(true);
    try {
      // 1) Upload the document with the AI metadata
      const fd = new FormData();
      fd.append('file', photoFile);
      fd.append('metadata_json', JSON.stringify(photoData || {}));
      const uploadRes = await axios.post(`${API}/api/documents/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const documentId = uploadRes.data?.id || null;

      // 2) Create the expense linked to that document
      await axios.post(
        `${API}/api/expenses`,
        {
          title: photoDraft.title,
          amount: amt,
          currency: photoDraft.currency,
          category: photoDraft.category,
          period: photoDraft.period,
          due_date: photoDraft.due_date || null,
          paid_by: photoDraft.paid_by || null,
          shared_with: photoDraft.shared_with || null,
          notes: photoDraft.notes || null,
          paid: photoDraft.paid,
          document_id: documentId,
        },
        { withCredentials: true }
      );
      toast.success('Gasto creado con foto adjunta');
      // If the period changed based on due_date, switch to it so the new expense is visible
      if (photoDraft.period !== period) setPeriod(photoDraft.period);
      closePhotoFlow();
      load(photoDraft.period || period);
    } catch {
      toast.error('No pudimos guardar el gasto');
    } finally {
      setPhotoSaving(false);
    }
  };

  const openPhotoPreview = (documentId) => {
    if (!documentId) return;
    setViewPhotoUrl(`${API}/api/documents/${documentId}/download`);
  };

  const openYearly = async (yr = yearlyYear) => {
    setShowYearlyModal(true);
    setYearlyLoading(true);
    setYearlyData(null);
    try {
      const { data } = await axios.get(`${API}/api/expenses/yearly?year=${yr}`, { withCredentials: true });
      setYearlyData(data);
    } catch {
      toast.error('Error al cargar la vista anual');
    } finally {
      setYearlyLoading(false);
    }
  };
  const changeYearlyYear = (delta) => {
    const nextYear = yearlyYear + delta;
    setYearlyYear(nextYear);
    openYearly(nextYear);
  };

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const [listRes, sumRes, budgetRes] = await Promise.all([
        axios.get(`${API}/api/expenses?period=${p}`, { withCredentials: true }),
        axios.get(`${API}/api/expenses/summary?period=${p}`, { withCredentials: true }),
        axios.get(`${API}/api/budgets/status?period=${p}`, { withCredentials: true }),
      ]);
      setItems(listRes.data || []);
      setSummary(sumRes.data || { total: 0, paid: 0, pending: 0, currency: 'ARS' });
      setBudgetStatus(budgetRes.data?.budgets || []);
      // Prefill drafts with current limits so the input shows the actual value
      const drafts = {};
      (budgetRes.data?.budgets || []).forEach((b) => { drafts[b.category] = String(b.limit); });
      setBudgetDraft((prev) => ({ ...drafts, ...prev }));
    } catch {
      toast.error('Error al cargar los gastos');
    } finally {
      setLoading(false);
    }
  }, []);

  const saveBudget = async (category) => {
    const raw = budgetDraft[category];
    const val = parseFloat(raw);
    if (isNaN(val) || val < 0) {
      toast.error('Ingresá un tope válido');
      return;
    }
    setBudgetSavingCat(category);
    try {
      if (val === 0) {
        await axios.delete(`${API}/api/budgets/${category}`, { withCredentials: true }).catch(() => {});
        toast.success('Presupuesto eliminado');
      } else {
        await axios.put(
          `${API}/api/budgets/${category}`,
          { category, limit: val, currency: summary.currency || 'ARS' },
          { withCredentials: true }
        );
        toast.success('Presupuesto guardado');
      }
      load(period);
    } catch {
      toast.error('No pudimos guardar el presupuesto');
    } finally {
      setBudgetSavingCat(null);
    }
  };

  useEffect(() => { load(period); }, [load, period]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((e) => {
      if (filter !== 'all' && e.category !== filter) return false;
      if (!q) return true;
      return (
        (e.title || '').toLowerCase().includes(q) ||
        (e.paid_by || '').toLowerCase().includes(q) ||
        (e.shared_with || '').toLowerCase().includes(q) ||
        (e.notes || '').toLowerCase().includes(q)
      );
    });
  }, [items, filter, query]);

  const openNew = () => {
    // F3 — default: última moneda usada; fallback a la principal.
    const last = readLastCurrency();
    const defaultCode = (last && enabledMap[last]) ? last : (currencyConfig.primary || 'ARS');
    setDraft({ ...emptyDraft(), period, currency: defaultCode });
    setEditingId(null);
    setShowForm(true);
  };
  const openEdit = (item) => {
    setDraft({
      title: item.title || '',
      amount: String(item.amount ?? ''),
      currency: item.currency || 'ARS',
      category: item.category,
      period: item.period,
      due_date: item.due_date || '',
      paid: !!item.paid,
      paid_by: item.paid_by || '',
      shared_with: item.shared_with || '',
      notes: item.notes || '',
      recurrent: !!item.recurrent,
      exchange_rate: item.exchange_rate != null ? String(item.exchange_rate) : '',
    });
    setEditingId(item.id);
    setShowForm(true);
  };
  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setDraft(emptyDraft());
  };

  const save = async (e) => {
    e.preventDefault();
    if (draft.title.trim().length < 2) return toast.error('El nombre del gasto es obligatorio');
    const amountNum = parseFloat(draft.amount);
    if (isNaN(amountNum) || amountNum < 0) return toast.error('Ingresá un monto válido');
    setSaving(true);
    try {
      // F3 — construir payload multidivisa. Sólo enviamos exchange_rate cuando
      // la moneda difiere de la principal y el usuario lo ingresó. Nunca inventamos.
      const { exchange_rate: rateStr, ...rest } = draft;
      const payload = { ...rest, amount: amountNum };
      const isForeign = draft.currency && draft.currency !== currencyConfig.primary;
      if (isForeign && rateStr !== '' && rateStr != null) {
        const rateNum = parseFloat(rateStr);
        if (!isNaN(rateNum) && rateNum > 0) payload.exchange_rate = rateNum;
      }
      if (editingId) {
        await axios.patch(`${API}/api/expenses/${editingId}`, payload, { withCredentials: true });
        toast.success('Gasto actualizado');
      } else {
        await axios.post(`${API}/api/expenses`, payload, { withCredentials: true });
        toast.success('Gasto creado');
      }
      // F3 — memorizar la última moneda usada para el siguiente alta.
      writeLastCurrency(draft.currency);
      closeForm();
      load(period);
    } catch {
      toast.error('No pudimos guardar el gasto');
    } finally {
      setSaving(false);
    }
  };

  const togglePaid = async (item) => {
    try {
      await axios.patch(
        `${API}/api/expenses/${item.id}`,
        { paid: !item.paid },
        { withCredentials: true }
      );
      load(period);
    } catch {
      toast.error('No pudimos actualizar el gasto');
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`¿Eliminar el gasto "${item.title}"?`)) return;
    try {
      await axios.delete(`${API}/api/expenses/${item.id}`, { withCredentials: true });
      toast.success('Gasto eliminado');
      load(period);
    } catch {
      toast.error('No pudimos eliminar el gasto');
    }
  };

  const openCopyModal = async () => {
    setShowCopyModal(true);
    setCopyLoading(true);
    setCopySource([]);
    setCopyDraft([]);
    try {
      const prev = shiftPeriod(period, -1);
      const { data } = await axios.get(`${API}/api/expenses?period=${prev}`, { withCredentials: true });
      setCopySource(data || []);
      // Default: all recurrent items selected, non-recurrent unselected
      setCopyDraft(
        (data || []).map((e) => ({
          source_id: e.id,
          selected: !!e.recurrent,
          title: e.title,
          amount: String(e.amount ?? ''),
          currency: e.currency || 'ARS',
          category: e.category,
          due_date: e.due_date ? shiftDate(e.due_date, 1) : '',
          paid_by: e.paid_by || '',
          shared_with: e.shared_with || '',
          notes: e.notes || '',
          recurrent: !!e.recurrent,
        }))
      );
    } catch {
      toast.error('No pudimos cargar el mes anterior');
    } finally {
      setCopyLoading(false);
    }
  };

  const toggleAllCopy = (checked) => {
    setCopyDraft((cur) => cur.map((r) => ({ ...r, selected: checked })));
  };

  const confirmCopy = async () => {
    const rows = copyDraft.filter((r) => r.selected);
    if (rows.length === 0) {
      toast.info('Elegí al menos un gasto para copiar');
      return;
    }
    setCopyBusy(true);
    let ok = 0;
    let fail = 0;
    for (const r of rows) {
      try {
        const amt = parseFloat(r.amount);
        if (isNaN(amt) || amt < 0) { fail += 1; continue; }
        await axios.post(
          `${API}/api/expenses`,
          {
            title: r.title,
            amount: amt,
            currency: r.currency,
            category: r.category,
            period,
            due_date: r.due_date || null,
            paid_by: r.paid_by || null,
            shared_with: r.shared_with || null,
            notes: r.notes || null,
            recurrent: r.recurrent,
            paid: false,
          },
          { withCredentials: true }
        );
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    setCopyBusy(false);
    setShowCopyModal(false);
    setCopySource([]);
    setCopyDraft([]);
    if (ok > 0) toast.success(`${ok} gasto${ok > 1 ? 's' : ''} copiado${ok > 1 ? 's' : ''} a ${periodLabel(period)}`);
    if (fail > 0) toast.error(`${fail} no se pudieron copiar`);
    load(period);
  };

  const pendingCount = items.filter((e) => !e.paid).length;

  return (
    <div className="space-y-6" data-testid="expenses-view">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-foreground mb-2" >
            Gastos
          </h1>
          <p className="text-muted-foreground">Llevá el control de facturas, tarjetas y pagos de la casa</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            data-testid="yearly-view-btn"
            onClick={() => openYearly(yearlyYear)}
            className="px-4 py-3 bg-surface border-2 border-foreground text-foreground rounded-full font-semibold hover:bg-[#1E3A5F] hover:text-white transition-all shadow-sm flex items-center gap-2 text-sm"
          >
            <BarChart3 className="w-4 h-4" /> Vista anual
          </button>
          <button
            data-testid="export-pdf-btn"
            onClick={exportPdf}
            className="px-4 py-3 bg-surface border-2 border-foreground text-foreground rounded-full font-semibold hover:bg-[#1E3A5F] hover:text-white transition-all shadow-sm flex items-center gap-2 text-sm"
          >
            <Download className="w-4 h-4" /> PDF
          </button>
          <button
            data-testid="export-csv-btn"
            onClick={exportCsv}
            className="px-4 py-3 bg-surface border-2 border-foreground text-foreground rounded-full font-semibold hover:bg-[#1E3A5F] hover:text-white transition-all shadow-sm flex items-center gap-2 text-sm"
          >
            <Download className="w-4 h-4" /> CSV
          </button>
          <button
            data-testid="copy-prev-month-btn"
            onClick={openCopyModal}
            className="px-4 py-3 bg-surface border-2 border-foreground text-foreground rounded-full font-semibold hover:bg-[#1E3A5F] hover:text-white transition-all shadow-sm flex items-center gap-2 text-sm"
          >
            <Copy className="w-4 h-4" /> Copiar mes anterior
          </button>
          <button
            data-testid="photo-expense-btn"
            onClick={openPhotoFlow}
            className="px-4 py-3 bg-[#166534] text-white rounded-full font-semibold hover:bg-[#14532d] transition-all shadow-md hover:-translate-y-0.5 flex items-center gap-2 text-sm"
          >
            <Camera className="w-4 h-4" /> Nuevo desde foto
          </button>
          <button
            data-testid="new-expense-btn"
            onClick={openNew}
            className="px-5 py-3 bg-[#D97706] text-white rounded-full font-semibold hover:bg-[#B45309] transition-all shadow-md hover:-translate-y-0.5 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Nuevo gasto
          </button>
        </div>
      </div>

      {/* Period selector + summary */}
      <div className="bg-surface rounded-2xl shadow-md p-5 sm:p-6">
        <div className="flex items-center justify-between mb-5">
          <button
            data-testid="prev-period-btn"
            onClick={() => setPeriod(shiftPeriod(period, -1))}
            className="p-2 hover:bg-muted rounded-full text-foreground"
            aria-label="Mes anterior"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="text-center">
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Período</p>
            <p className="text-xl sm:text-2xl font-bold text-foreground"  data-testid="period-label">
              {periodLabel(period)}
            </p>
          </div>
          <button
            data-testid="next-period-btn"
            onClick={() => setPeriod(shiftPeriod(period, 1))}
            className="p-2 hover:bg-muted rounded-full text-foreground"
            aria-label="Mes siguiente"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="p-4 rounded-2xl bg-accent/10 dark:bg-accent/20 border border-accent/20 dark:border-accent/40">
            <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-accent">Total mes</p>
            <p className="text-lg sm:text-2xl font-bold text-foreground dark:text-white tabular-nums" data-testid="sum-total">
              {formatMoney(
                (summary.total_in_primary != null ? summary.total_in_primary : summary.total),
                (summary.primary_currency || summary.currency)
              )}
            </p>
            {(summary.previous_total > 0 || summary.total > 0) && (() => {
              const diff = summary.diff || 0;
              const pct = summary.pct_change;
              const up = diff > 0;
              const flat = diff === 0;
              const label = pct === null || pct === undefined
                ? (summary.previous_total > 0 ? '' : 'sin mes anterior')
                : `${up ? '+' : ''}${pct}%`;
              const colorCls = flat
                ? 'text-muted-foreground'
                : up
                ? 'text-red-600 dark:text-red-400'
                : 'text-emerald-700 dark:text-emerald-400';
              return (
                <p
                  data-testid="sum-comparison"
                  className={`text-[10px] sm:text-xs font-semibold mt-1 ${colorCls}`}
                  title={`Mes pasado (${summary.previous_period}): ${formatMoney(summary.previous_total, (summary.primary_currency || summary.currency))}`}
                >
                  {flat && summary.previous_total > 0
                    ? '= igual al mes pasado'
                    : label
                    ? `${up ? '▲' : '▼'} ${label} vs mes pasado`
                    : 'sin mes anterior'}
                </p>
              );
            })()}
          </div>
          <div className="p-4 rounded-2xl bg-emerald-100 dark:bg-emerald-500/20 border border-emerald-500/20 dark:border-emerald-400/40">
            <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-emerald-800 dark:text-emerald-300">Pagado</p>
            <p className="text-lg sm:text-2xl font-bold text-emerald-800 dark:text-emerald-200" data-testid="sum-paid">
              {formatMoney(
                (summary.paid_in_primary != null ? summary.paid_in_primary : summary.paid),
                (summary.primary_currency || summary.currency)
              )}
            </p>
          </div>
          <div className="p-4 rounded-2xl bg-amber-100 dark:bg-amber-500/20 border border-amber-500/20 dark:border-amber-400/40">
            <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-amber-800 dark:text-amber-300">Pendiente</p>
            <p className="text-lg sm:text-2xl font-bold text-amber-800 dark:text-amber-200" data-testid="sum-pending">
              {formatMoney(
                (summary.pending_in_primary != null ? summary.pending_in_primary : summary.pending),
                (summary.primary_currency || summary.currency)
              )}
            </p>
            {pendingCount > 0 && (
              <p className="text-[10px] text-amber-700 dark:text-amber-300/80 mt-0.5">{pendingCount} sin pagar</p>
            )}
          </div>
        </div>

        {(() => {
          // F3/F4 multi-moneda:
          //  - Si hay 2+ monedas o hay gastos sin convertir → mostrar desglose por moneda.
          //  - Cada card muestra pagado/pendiente en la moneda original.
          //  - Si el bloque `converted` está disponible, agregamos preview convertido a la principal.
          //  - Al final, advertencia si hay gastos sin conversión (no se sumaron al total principal).
          const bc = summary.by_currency || {};
          const codes = Object.keys(bc);
          const primary = summary.primary_currency || summary.currency;
          const unconverted = summary.unconverted_count || 0;
          const hasForeign = codes.some((c) => c !== primary);
          if (codes.length < 2 && unconverted === 0 && !hasForeign) return null;
          return (
            <div className="mt-3 p-4 rounded-2xl bg-surface-2 border border-border space-y-3" data-testid="sum-by-currency">
              <div className="flex items-baseline justify-between">
                <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-muted-foreground">Totales por moneda</p>
                {summary.total_in_primary != null && (
                  <p className="text-[11px] text-muted-foreground tabular-nums" data-testid="sum-total-primary">
                    Total en <span className="font-semibold text-foreground">{primary}</span>: <span className="font-semibold text-foreground">{formatMoney(summary.total_in_primary, primary)}</span>
                  </p>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {codes.sort().map((code) => {
                  const b = bc[code] || {};
                  const isPrimary = code === primary;
                  const conv = b.converted;
                  return (
                    <div key={code} data-testid={`sum-currency-${code}`}
                         className={`p-3 rounded-2xl bg-surface border ${isPrimary ? 'border-primary/30 ring-1 ring-primary/10' : 'border-border'}`}>
                      <div className="flex items-baseline justify-between">
                        <p className="text-[11px] font-bold uppercase tracking-wider text-foreground tabular-nums">{code}</p>
                        {isPrimary && <span className="text-[9px] font-bold uppercase tracking-wider text-primary">Principal</span>}
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                        <span className="text-xs text-emerald-700 dark:text-emerald-400 tabular-nums" data-testid={`sum-currency-${code}-paid`}>
                          Pagado {formatMoney(b.pagado, code)}
                        </span>
                        <span className="text-xs text-amber-700 dark:text-amber-400 tabular-nums" data-testid={`sum-currency-${code}-pending`}>
                          Pendiente {formatMoney(b.pendiente, code)}
                        </span>
                      </div>
                      {!isPrimary && conv && conv.total != null && (
                        <p className="text-[11px] text-muted-foreground mt-1.5 tabular-nums" data-testid={`sum-currency-${code}-converted`}>
                          ≈ {formatMoney(conv.total, primary)} <span className="text-muted-foreground/70">convertidos</span>
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
              {unconverted > 0 && (
                <div
                  className="flex items-start gap-2.5 p-3 rounded-2xl bg-amber-50 dark:bg-amber-500/10 border border-amber-300/50 dark:border-amber-400/25"
                  data-testid="sum-unconverted-warning"
                >
                  <span className="text-amber-600 dark:text-amber-400 leading-none pt-0.5">⚠</span>
                  <div className="text-[12px] text-amber-800 dark:text-amber-200 leading-snug">
                    <b>{unconverted}</b> gasto{unconverted === 1 ? '' : 's'} sin tipo de cambio — no se {unconverted === 1 ? 'sumó' : 'sumaron'} al total en {primary}.
                    <span className="text-amber-700/80 dark:text-amber-300/80"> Editalos e ingresá el rate para incluirlos.</span>
                  </div>
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Filters */}
      <div className="bg-surface rounded-2xl shadow-md p-4 sm:p-5 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            data-testid="expense-search"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar gasto, quién pagó, notas..."
            className="w-full pl-10 pr-4 py-2 bg-surface-2 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#D97706]"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
          <button
            data-testid="expense-cat-filter-all"
            onClick={() => setFilter('all')}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold flex-shrink-0 transition-colors ${filter === 'all' ? 'bg-primary text-primary-foreground shadow-md shadow-primary/30' : 'bg-muted text-muted-foreground hover:bg-surface-2'}`}
          >
            Todos
          </button>
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              data-testid={`expense-cat-filter-${c.key}`}
              onClick={() => setFilter(c.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold flex-shrink-0 flex items-center gap-1.5 transition-colors ${filter === c.key ? 'bg-primary text-primary-foreground shadow-md shadow-primary/30' : c.color}`}
            >
              <c.icon className="w-3.5 h-3.5" strokeWidth={2} /> {c.label}
            </button>
          ))}
        </div>
      </div>

      {/* Gráficos siempre visibles: torta por categoría + barras últimos 6 meses */}
      <ExpenseChartsSection period={period} />

      {showForm && (
        <form
          data-testid="expense-form"
          onSubmit={save}
          className="bg-surface rounded-2xl shadow-lg p-6 sm:p-8 space-y-4 border-2 border-[#D97706]/30"
        >
          <div className="flex items-center justify-between">
            <h2 className="font-bold text-foreground">{editingId ? 'Editar gasto' : 'Nuevo gasto'}</h2>
            <button type="button" onClick={closeForm} className="p-2 hover:bg-muted rounded-full text-muted-foreground">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-muted-foreground mb-2">Nombre del gasto</label>
              <input
                data-testid="expense-title-input"
                type="text" required
                value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                className="w-full px-4 py-3 bg-surface-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#D97706]"
                placeholder="Ej: Factura de luz, Colegio Sofía..."
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-muted-foreground mb-2">Monto</label>
              <div className="flex gap-2">
                <select
                  data-testid="expense-currency-select"
                  value={draft.currency}
                  onChange={(e) => {
                    const newCode = e.target.value;
                    // F3 — al cambiar de moneda, limpiar el rate previo (evita rates fantasmas
                    // heredados de la moneda anterior). El usuario debe ingresarlo de nuevo si aplica.
                    setDraft((d) => ({ ...d, currency: newCode, exchange_rate: '' }));
                  }}
                  className="w-24 px-3 py-3 bg-surface-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#D97706] font-semibold tabular-nums"
                >
                  {(currencyConfig.enabled || []).map((c) => (
                    <option key={c.code} value={c.code}>{c.code}</option>
                  ))}
                </select>
                <input
                  data-testid="expense-amount-input"
                  type="number" step="0.01" min="0" required
                  value={draft.amount} onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
                  className="flex-1 px-4 py-3 bg-surface-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#D97706]"
                  placeholder="0"
                />
              </div>

              {/* F3 — Campo de tipo de cambio: sólo cuando la moneda difiere de la principal.
                  Nunca autocompletamos ni consultamos: es opcional y lo ingresa el usuario. */}
              {draft.currency && draft.currency !== currencyConfig.primary && (
                <div className="mt-3 pl-1" data-testid="exchange-rate-block">
                  <div className="flex items-baseline justify-between mb-1.5">
                    <label className="text-[12px] font-semibold text-muted-foreground tracking-tight">
                      Tipo de cambio <span className="font-normal text-muted-foreground/70">(opcional)</span>
                    </label>
                    <span className="text-[11px] text-muted-foreground/70 tabular-nums">
                      1 {draft.currency} = ? {currencyConfig.primary}
                    </span>
                  </div>
                  <div className="relative">
                    <input
                      data-testid="expense-exchange-rate-input"
                      type="number" step="0.0001" min="0"
                      value={draft.exchange_rate}
                      onChange={(e) => setDraft({ ...draft, exchange_rate: e.target.value })}
                      className="w-full pl-4 pr-3 py-2.5 text-[14px] bg-surface-2 rounded-2xl
                                 border border-transparent focus:outline-none
                                 focus:ring-1 focus:ring-primary/40 focus:border-primary/40
                                 tabular-nums placeholder:text-muted-foreground/50"
                      placeholder={`¿Cuánto equivale 1 ${draft.currency} en ${currencyConfig.primary}?`}
                    />
                  </div>
                  {/* Preview discreto de la conversión */}
                  {draft.exchange_rate && parseFloat(draft.exchange_rate) > 0 && draft.amount && parseFloat(draft.amount) > 0 && (
                    <div
                      className="mt-2 text-[12px] text-muted-foreground tabular-nums flex items-center gap-2"
                      data-testid="exchange-rate-preview"
                    >
                      <span className="font-medium text-foreground/80">
                        {Number(draft.amount).toLocaleString('es-AR', { maximumFractionDigits: 0 })} {draft.currency}
                      </span>
                      <span className="text-muted-foreground/60">→</span>
                      <span className="font-semibold text-foreground">
                        {formatConverted(parseFloat(draft.amount) * parseFloat(draft.exchange_rate), currencyConfig.primary)}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold text-muted-foreground mb-2">Categoría</label>
              <select
                data-testid="expense-category-select"
                value={draft.category}
                onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                className="w-full px-4 py-3 bg-surface-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#D97706]"
              >
                {CATEGORIES.map((c) => (<option key={c.key} value={c.key}>{c.label}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-muted-foreground mb-2">Período (mes)</label>
              <input
                data-testid="expense-period-input"
                type="month" required
                value={draft.period}
                onChange={(e) => setDraft({ ...draft, period: e.target.value })}
                className="w-full px-4 py-3 bg-surface-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#D97706]"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-muted-foreground mb-2">Vencimiento</label>
              <input
                data-testid="expense-duedate-input"
                type="date"
                value={draft.due_date}
                onChange={(e) => setDraft({ ...draft, due_date: e.target.value })}
                className="date-es w-full px-4 py-3 bg-surface-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#D97706]"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-muted-foreground mb-2">Pagado por</label>
              <input
                data-testid="expense-paidby-input"
                type="text"
                value={draft.paid_by}
                onChange={(e) => setDraft({ ...draft, paid_by: e.target.value })}
                className="w-full px-4 py-3 bg-surface-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#D97706]"
                placeholder="Ej: Papá, yo, entre los 2"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-muted-foreground mb-2">Compartido con</label>
              <input
                data-testid="expense-sharedwith-input"
                type="text"
                value={draft.shared_with}
                onChange={(e) => setDraft({ ...draft, shared_with: e.target.value })}
                className="w-full px-4 py-3 bg-surface-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#D97706]"
                placeholder="Ej: Toda la familia, Sofía"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-muted-foreground mb-2">Notas</label>
              <textarea
                data-testid="expense-notes-input"
                rows={2} value={draft.notes}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                className="w-full px-4 py-3 bg-surface-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#D97706] resize-none"
                placeholder="Nº de comprobante, forma de pago, aclaraciones..."
              />
            </div>
            <div className="sm:col-span-2 flex flex-wrap gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  data-testid="expense-paid-toggle"
                  type="checkbox"
                  checked={draft.paid}
                  onChange={(e) => setDraft({ ...draft, paid: e.target.checked })}
                  className="w-5 h-5 accent-[#4CAF50]"
                />
                <span className="text-sm font-semibold text-muted-foreground">Ya está pagado</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  data-testid="expense-recurrent-toggle"
                  type="checkbox"
                  checked={draft.recurrent}
                  onChange={(e) => setDraft({ ...draft, recurrent: e.target.checked })}
                  className="w-5 h-5 accent-[#1E3A5F]"
                />
                <span className="text-sm font-semibold text-muted-foreground flex items-center gap-1">
                  <Repeat className="w-4 h-4" /> Se repite todos los meses
                </span>
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={closeForm} className="px-4 py-2 text-muted-foreground hover:bg-muted rounded-full">Cancelar</button>
            <button
              type="submit" disabled={saving} data-testid="expense-save-btn"
              className="px-6 py-2 bg-[#1E3A5F] text-white rounded-full font-semibold hover:bg-[#152B47] disabled:opacity-50"
            >
              {saving ? 'Guardando...' : editingId ? 'Actualizar' : 'Guardar gasto'}
            </button>
          </div>
        </form>
      )}

      {/* List */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="w-10 h-10 border-4 border-foreground border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-surface rounded-2xl shadow-md p-10 text-center">
          <Wallet className="w-12 h-12 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-muted-foreground">
            {items.length === 0
              ? `No hay gastos cargados en ${periodLabel(period)}`
              : 'No hay gastos que coincidan con el filtro'}
          </p>
        </div>
      ) : (
        <div className="bg-surface rounded-2xl shadow-md overflow-hidden">
          {filtered.map((e, idx) => {
            const cat = CATEGORY_MAP[e.category] || CATEGORY_MAP.otros;
            const Icon = cat.icon;
            const overdue = isOverdue(e);
            const estado = expenseStatus(e);
            const estadoEmoji = statusEmoji(estado);
            const estadoLabel = statusLabel(estado);
            // Estado visible en la tarjeta — misma fuente de verdad que
            // Wingman y WhatsApp. Ocultamos sólo `sin_fecha` (ruido).
            const showEstado = estado && estado !== 'sin_fecha';
            const estadoBadgeClass = {
              vencido: 'text-red-700 bg-red-100 dark:bg-red-500/20 dark:text-red-300',
              vence_hoy: 'text-orange-700 bg-orange-100 dark:bg-orange-500/20 dark:text-orange-300',
              proximo: 'text-yellow-800 bg-yellow-100 dark:bg-yellow-500/20 dark:text-yellow-200',
              pagado: 'text-emerald-700 bg-emerald-100 dark:bg-emerald-500/20 dark:text-emerald-300',
            }[estado] || 'text-slate-700 bg-slate-100';
            return (
              <div
                key={e.id}
                data-testid={`expense-${e.id}`}
                className={`flex flex-wrap items-center gap-3 p-4 sm:p-5 ${
                  idx > 0 ? 'border-t border-slate-100' : ''
                } ${e.paid ? 'bg-surface-2/50' : overdue ? 'bg-red-50/40' : ''}`}
              >
                <button
                  onClick={() => togglePaid(e)}
                  data-testid={`expense-toggle-${e.id}`}
                  title={e.paid ? 'Marcar como pendiente' : 'Marcar como pagado'}
                  className={`w-9 h-9 rounded-full flex items-center justify-center border-2 flex-shrink-0 ${
                    e.paid
                      ? 'bg-[#4CAF50] border-[#4CAF50] text-white'
                      : 'border-border hover:border-[#4CAF50] text-transparent hover:text-emerald-600 dark:text-emerald-400'
                  }`}
                >
                  <Check className="w-5 h-5" strokeWidth={3} />
                </button>

                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${cat.color}`}>
                  <Icon className="w-5 h-5" strokeWidth={1.75} />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className={`font-bold text-foreground ${e.paid ? 'line-through opacity-60' : ''}`}>{e.title}</p>
                    {e.recurrent && (
                      <span title="Recurrente" className="text-muted-foreground"><Repeat className="w-3.5 h-3.5" /></span>
                    )}
                    {showEstado && (
                      <span
                        data-testid={`expense-status-${e.id}`}
                        data-status={estado}
                        className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${estadoBadgeClass}`}
                      >
                        <span aria-hidden="true">{estadoEmoji}</span> {estadoLabel}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-3 text-xs text-muted-foreground mt-1">
                    <span>{cat.label}</span>
                    {e.due_date && <span>Vence: {e.due_date}</span>}
                    {e.paid_by && <span>Pagó: {e.paid_by}</span>}
                    {e.shared_with && <span>Compartido: {e.shared_with}</span>}
                  </div>
                  {e.variation && e.variation.has_prev && (
                    <div className="mt-1.5">
                      <VariationChip variation={e.variation} testId={`expense-variation-${e.id}`} />
                    </div>
                  )}
                  {e.notes && <p className="text-xs text-muted-foreground mt-1 truncate">{e.notes}</p>}
                </div>

                <p className={`text-lg font-bold ${e.paid ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
                  {formatMoney(e.amount, e.currency)}
                </p>

                <div className="flex items-center gap-1 ml-auto sm:ml-0">
                  {e.document_id && (
                    <button
                      data-testid={`expense-photo-${e.id}`}
                      onClick={() => openPhotoPreview(e.document_id)}
                      title="Ver comprobante"
                      className="p-2 hover:bg-sky-100 dark:bg-sky-500/15 rounded-full text-foreground"
                    >
                      <ImageIcon className="w-4 h-4" />
                    </button>
                  )}
                  <ShareWhatsappButton
                    title={`${e.title} — ${formatMoney(e.amount, e.currency)}`}
                    date={e.due_date}
                    description={[
                      cat.label,
                      e.paid ? 'Pagado' : 'Pendiente',
                      e.shared_with && `Compartido con ${e.shared_with}`,
                    ].filter(Boolean).join(' · ')}
                    kind={e.paid ? 'reminder' : 'bill'}
                    amount={e.amount}
                    currency={e.currency}
                    testId={`expense-share-${e.id}`}
                  />
                  <button
                    onClick={() => openEdit(e)}
                    data-testid={`expense-edit-${e.id}`}
                    title="Editar"
                    className="p-2 hover:bg-muted rounded-full text-muted-foreground"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => remove(e)}
                    data-testid={`expense-delete-${e.id}`}
                    title="Eliminar"
                    className="p-2 hover:bg-red-50 rounded-full text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Copy previous month modal */}
      {showCopyModal && (
        <div
          data-testid="copy-modal"
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-2 sm:p-4"
          onClick={(e) => e.target === e.currentTarget && !copyBusy && setShowCopyModal(false)}
        >
          <div className="bg-surface rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-slate-100">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-primary">Copiar mes anterior</p>
                <h2 className="text-xl font-bold text-foreground" >
                  {periodLabel(shiftPeriod(period, -1))} → {periodLabel(period)}
                </h2>
                <p className="text-xs text-muted-foreground mt-1">Elegí qué gastos copiar y ajustá los montos antes de confirmar</p>
              </div>
              <button
                onClick={() => !copyBusy && setShowCopyModal(false)}
                className="p-2 hover:bg-muted rounded-full text-muted-foreground"
                aria-label="Cerrar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-5">
              {copyLoading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="w-10 h-10 border-4 border-foreground border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : copySource.length === 0 ? (
                <div className="text-center py-12">
                  <Copy className="w-12 h-12 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
                  <p className="text-muted-foreground">No hay gastos en {periodLabel(shiftPeriod(period, -1))}</p>
                  <p className="text-muted-foreground text-sm mt-1">Cargá gastos en algún mes antes de poder copiarlos</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-3 pb-3 border-b border-slate-100">
                    <label className="flex items-center gap-2 text-sm font-semibold text-muted-foreground cursor-pointer">
                      <input
                        data-testid="copy-select-all"
                        type="checkbox"
                        checked={copyDraft.length > 0 && copyDraft.every((r) => r.selected)}
                        onChange={(e) => toggleAllCopy(e.target.checked)}
                        className="w-4 h-4 accent-[#1E3A5F]"
                      />
                      Seleccionar todos
                    </label>
                    <p className="text-xs text-muted-foreground">
                      {copyDraft.filter((r) => r.selected).length} de {copyDraft.length} seleccionados
                    </p>
                  </div>
                  <div className="space-y-2">
                    {copyDraft.map((row, idx) => {
                      const cat = CATEGORY_MAP[row.category] || CATEGORY_MAP.otros;
                      const Icon = cat.icon;
                      return (
                        <div
                          key={row.source_id}
                          data-testid={`copy-row-${row.source_id}`}
                          className={`p-3 rounded-2xl border-2 transition-all ${
                            row.selected ? 'border-[#D97706] bg-amber-100 dark:bg-amber-500/15/30' : 'border-slate-100 bg-surface'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <input
                              data-testid={`copy-check-${row.source_id}`}
                              type="checkbox"
                              checked={row.selected}
                              onChange={(e) => {
                                const next = [...copyDraft];
                                next[idx] = { ...next[idx], selected: e.target.checked };
                                setCopyDraft(next);
                              }}
                              className="w-5 h-5 accent-[#1E3A5F] flex-shrink-0"
                            />
                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${cat.color}`}>
                              <Icon className="w-4 h-4" strokeWidth={2} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-foreground truncate">{row.title}</p>
                              <p className="text-[10px] text-muted-foreground">
                                {cat.label}{row.recurrent && ' · Recurrente'}
                              </p>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <span className="text-xs font-semibold text-muted-foreground">{row.currency}</span>
                              <input
                                data-testid={`copy-amount-${row.source_id}`}
                                type="number"
                                min="0" step="0.01"
                                value={row.amount}
                                disabled={!row.selected}
                                onChange={(e) => {
                                  const next = [...copyDraft];
                                  next[idx] = { ...next[idx], amount: e.target.value };
                                  setCopyDraft(next);
                                }}
                                className="w-28 sm:w-32 px-3 py-2 bg-surface-2 rounded-lg text-right text-sm font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-[#D97706] disabled:opacity-50"
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="p-5 border-t border-slate-100 flex items-center justify-end gap-2">
              <button
                onClick={() => setShowCopyModal(false)}
                disabled={copyBusy}
                className="px-4 py-2 text-muted-foreground hover:bg-muted rounded-full disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                data-testid="copy-confirm-btn"
                onClick={confirmCopy}
                disabled={copyBusy || copyDraft.filter((r) => r.selected).length === 0}
                className="px-6 py-2 bg-[#4CAF50] text-white rounded-full font-semibold hover:bg-[#3d8b40] disabled:opacity-50 flex items-center gap-2"
              >
                {copyBusy ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Copiando...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" strokeWidth={3} />
                    Copiar {copyDraft.filter((r) => r.selected).length} gasto{copyDraft.filter((r) => r.selected).length !== 1 ? 's' : ''}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Yearly view modal */}
      {showYearlyModal && (
        <div
          data-testid="yearly-modal"
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-2 sm:p-4"
          onClick={(e) => e.target === e.currentTarget && setShowYearlyModal(false)}
        >
          <div className="bg-surface rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <button
                  data-testid="yearly-prev"
                  onClick={() => changeYearlyYear(-1)}
                  className="p-2 hover:bg-muted rounded-full text-foreground"
                  aria-label="Año anterior"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="text-center">
                  <p className="text-xs font-bold uppercase tracking-widest text-primary">Vista anual</p>
                  <h2 className="text-xl sm:text-2xl font-bold text-foreground"  data-testid="yearly-year">
                    {yearlyYear}
                  </h2>
                </div>
                <button
                  data-testid="yearly-next"
                  onClick={() => changeYearlyYear(1)}
                  className="p-2 hover:bg-muted rounded-full text-foreground"
                  aria-label="Año siguiente"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
              <button
                onClick={() => setShowYearlyModal(false)}
                className="p-2 hover:bg-muted rounded-full text-muted-foreground"
                aria-label="Cerrar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {yearlyLoading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="w-10 h-10 border-4 border-foreground border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : !yearlyData ? (
                <p className="text-center text-muted-foreground py-8">Sin datos</p>
              ) : (() => {
                const cur = yearlyData.currency || 'ARS';
                const fmt = (n) => `${cur} ${Number(n || 0).toLocaleString('es-AR', { maximumFractionDigits: 0 })}`;
                const months = Object.entries(yearlyData.by_month);
                const maxTotal = Math.max(1, ...months.map(([, v]) => v.total));
                const monthLabels = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
                return (
                  <div className="space-y-6">
                    {/* Summary */}
                    <div className="grid grid-cols-3 gap-3">
                      <div className="p-4 rounded-2xl bg-sky-100 dark:bg-sky-500/15 border border-foreground/10">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-foreground">Total año</p>
                        <p className="text-lg sm:text-xl font-bold text-foreground" data-testid="yearly-total">{fmt(yearlyData.total)}</p>
                      </div>
                      <div className="p-4 rounded-2xl bg-emerald-100 dark:bg-emerald-500/15 border border-[#4CAF50]/20">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">Pagado</p>
                        <p className="text-lg sm:text-xl font-bold text-emerald-700 dark:text-emerald-300">{fmt(yearlyData.paid)}</p>
                      </div>
                      <div className="p-4 rounded-2xl bg-amber-100 dark:bg-amber-500/15 border border-[#D97706]/20">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-amber-800 dark:text-amber-300">Pendiente</p>
                        <p className="text-lg sm:text-xl font-bold text-amber-800 dark:text-amber-300">{fmt(yearlyData.pending)}</p>
                      </div>
                    </div>

                    {/* Bar chart per month */}
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">Gasto por mes</p>
                      <div className="grid grid-cols-6 sm:grid-cols-12 gap-2 items-end h-40 sm:h-52">
                        {months.map(([key, v], idx) => {
                          const paidH = maxTotal > 0 ? (v.paid / maxTotal) * 100 : 0;
                          const pendH = maxTotal > 0 ? (v.pending / maxTotal) * 100 : 0;
                          return (
                            <div key={key} className="flex flex-col items-center justify-end h-full" data-testid={`bar-${key}`}>
                              <div className="w-full flex flex-col justify-end h-full rounded-t overflow-hidden bg-surface-2">
                                {v.pending > 0 && (
                                  <div
                                    title={`Pendiente ${fmt(v.pending)}`}
                                    style={{ height: `${pendH}%` }}
                                    className="w-full bg-[#D97706]"
                                  />
                                )}
                                {v.paid > 0 && (
                                  <div
                                    title={`Pagado ${fmt(v.paid)}`}
                                    style={{ height: `${paidH}%` }}
                                    className="w-full bg-[#4CAF50]"
                                  />
                                )}
                              </div>
                              <p className="mt-1 text-[10px] font-semibold text-muted-foreground">{monthLabels[idx]}</p>
                            </div>
                          );
                        })}
                      </div>
                      <div className="mt-3 flex gap-4 justify-center text-[11px] text-muted-foreground">
                        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-[#4CAF50] rounded" /> Pagado</span>
                        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-[#D97706] rounded" /> Pendiente</span>
                      </div>
                    </div>

                    {/* Monthly detail table */}
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Detalle mensual</p>
                      <div className="divide-y divide-slate-100 border border-slate-100 rounded-2xl overflow-hidden">
                        {months.map(([key, v], idx) => (
                          <div
                            key={key}
                            className="flex items-center px-4 py-2.5 text-sm hover:bg-surface-2 cursor-pointer"
                            onClick={() => { setPeriod(key); setShowYearlyModal(false); }}
                            data-testid={`yearly-row-${key}`}
                          >
                            <span className="w-16 sm:w-24 text-muted-foreground font-semibold">{monthLabels[idx]}</span>
                            <span className="w-16 text-muted-foreground text-xs">{v.count} ítems</span>
                            <span className="flex-1 text-right text-emerald-700 dark:text-emerald-300 font-semibold text-xs">{fmt(v.paid)}</span>
                            <span className="w-24 text-right text-amber-800 dark:text-amber-300 font-semibold text-xs">{fmt(v.pending)}</span>
                            <span className="w-28 text-right text-foreground font-bold">{fmt(v.total)}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* By category */}
                    {Object.keys(yearlyData.by_category).length > 0 && (
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Por categoría</p>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {Object.entries(yearlyData.by_category)
                            .sort((a, b) => b[1] - a[1])
                            .map(([cat, amt]) => {
                              const c = CATEGORY_MAP[cat] || CATEGORY_MAP.otros;
                              const Icon = c.icon;
                              return (
                                <div key={cat} className={`p-3 rounded-lg ${c.color}`}>
                                  <div className="flex items-center gap-2 mb-1">
                                    <Icon className="w-4 h-4" strokeWidth={2} />
                                    <p className="text-[10px] font-bold uppercase tracking-wider">{c.label}</p>
                                  </div>
                                  <p className="text-sm font-bold">{fmt(amt)}</p>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
      {/* Photo → AI → Expense modal */}
      {photoModalOpen && (
        <div
          data-testid="photo-modal"
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-2 sm:p-4"
          onClick={(e) => e.target === e.currentTarget && !photoSaving && closePhotoFlow()}
        >
          <div className="bg-surface rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-slate-100">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-emerald-700 dark:text-emerald-300">Nuevo gasto desde foto</p>
                <h2 className="text-xl font-bold text-foreground" >
                  📸 Comprobante → IA → Gasto
                </h2>
              </div>
              <button
                onClick={closePhotoFlow}
                disabled={photoSaving}
                className="p-2 hover:bg-muted rounded-full text-muted-foreground disabled:opacity-50"
                aria-label="Cerrar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <input
              ref={photoInputRef}
              data-testid="photo-file-input"
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={handlePhotoSelected}
            />

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {!photoFile ? (
                <button
                  onClick={() => photoInputRef.current?.click()}
                  data-testid="photo-picker-btn"
                  className="w-full py-16 border-2 border-dashed border-border rounded-2xl hover:border-[#166534] hover:bg-emerald-100 dark:bg-emerald-500/15/40 transition-all flex flex-col items-center gap-3 text-muted-foreground hover:text-emerald-700 dark:text-emerald-300"
                >
                  <Camera className="w-12 h-12" strokeWidth={1.5} />
                  <p className="font-semibold">Sacar foto o elegir del celular</p>
                  <p className="text-xs">Vamos a leer el comprobante y crear el gasto automáticamente</p>
                </button>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <img
                      src={photoPreviewUrl}
                      alt="Comprobante"
                      data-testid="photo-preview-thumb"
                      className="w-full h-48 sm:h-full object-cover rounded-2xl border border-border bg-surface-2"
                    />
                    <div>
                      {photoAnalyzing ? (
                        <div className="flex flex-col items-center justify-center py-8 gap-3">
                          <div className="flex items-center gap-2 text-foreground font-semibold">
                            <Sparkles className="w-5 h-5 animate-pulse text-primary" />
                            Analizando con IA...
                          </div>
                          <div className="w-8 h-8 border-4 border-foreground border-t-transparent rounded-full animate-spin"></div>
                          <p className="text-xs text-muted-foreground text-center">
                            Extrayendo servicio, monto, fecha y categoría
                          </p>
                        </div>
                      ) : photoData ? (
                        <div className="bg-emerald-100 dark:bg-emerald-500/15 border border-[#4CAF50]/30 rounded-2xl p-3 space-y-2 text-xs">
                          <div className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-emerald-700 dark:text-emerald-300" />
                            <p className="font-bold text-emerald-700 dark:text-emerald-300 uppercase tracking-wider">IA detectó</p>
                          </div>
                          {photoData.document_type && (
                            <p><b>Tipo:</b> {photoData.document_type}</p>
                          )}
                          {photoData.action_required && (
                            <p><b>Qué hacer:</b> {photoData.action_required}</p>
                          )}
                          {photoData.assigned_to && (
                            <p><b>Para:</b> {photoData.assigned_to}</p>
                          )}
                          {photoData.items_to_bring && (
                            <p><b>Llevar:</b> {photoData.items_to_bring}</p>
                          )}
                          <p className="text-[10px] text-muted-foreground pt-1 border-t border-[#4CAF50]/20">
                            Revisá y ajustá los datos abajo antes de guardar
                          </p>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  {photoDraft && !photoAnalyzing && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-semibold text-muted-foreground mb-1">Nombre del gasto</label>
                        <input
                          data-testid="photo-title-input"
                          type="text"
                          value={photoDraft.title}
                          onChange={(e) => setPhotoDraft({ ...photoDraft, title: e.target.value })}
                          className="w-full px-3 py-2 bg-surface-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#166534]"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-muted-foreground mb-1">Monto</label>
                        <div className="flex gap-1">
                          <select
                            value={photoDraft.currency}
                            onChange={(e) => setPhotoDraft({ ...photoDraft, currency: e.target.value })}
                            className="w-20 px-2 py-2 bg-surface-2 rounded-lg text-sm"
                          >
                            <option>ARS</option><option>USD</option><option>EUR</option>
                          </select>
                          <input
                            data-testid="photo-amount-input"
                            type="number" min="0" step="0.01"
                            value={photoDraft.amount}
                            onChange={(e) => setPhotoDraft({ ...photoDraft, amount: e.target.value })}
                            className="flex-1 px-3 py-2 bg-surface-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#166534]"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-muted-foreground mb-1">Categoría</label>
                        <select
                          data-testid="photo-category-select"
                          value={photoDraft.category}
                          onChange={(e) => setPhotoDraft({ ...photoDraft, category: e.target.value })}
                          className="w-full px-3 py-2 bg-surface-2 rounded-lg text-sm"
                        >
                          {CATEGORIES.map((c) => (<option key={c.key} value={c.key}>{c.label}</option>))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-muted-foreground mb-1">Período</label>
                        <input
                          type="month"
                          value={photoDraft.period}
                          onChange={(e) => setPhotoDraft({ ...photoDraft, period: e.target.value })}
                          className="w-full px-3 py-2 bg-surface-2 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-muted-foreground mb-1">Vencimiento</label>
                        <input
                          type="date"
                          value={photoDraft.due_date}
                          onChange={(e) => setPhotoDraft({ ...photoDraft, due_date: e.target.value })}
                          className="date-es w-full px-3 py-2 bg-surface-2 rounded-lg text-sm"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-semibold text-muted-foreground mb-1">Notas</label>
                        <textarea
                          rows={2}
                          value={photoDraft.notes}
                          onChange={(e) => setPhotoDraft({ ...photoDraft, notes: e.target.value })}
                          className="w-full px-3 py-2 bg-surface-2 rounded-lg text-sm resize-none"
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="p-5 border-t border-slate-100 flex items-center justify-between gap-2">
              <button
                onClick={() => photoInputRef.current?.click()}
                disabled={photoSaving || photoAnalyzing}
                className="text-sm text-foreground font-semibold px-3 py-2 hover:bg-muted rounded-full disabled:opacity-50"
              >
                Cambiar foto
              </button>
              <div className="flex gap-2">
                <button
                  onClick={closePhotoFlow}
                  disabled={photoSaving}
                  className="px-4 py-2 text-muted-foreground hover:bg-muted rounded-full disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  data-testid="photo-confirm-btn"
                  onClick={confirmPhotoExpense}
                  disabled={photoSaving || photoAnalyzing || !photoDraft}
                  className="px-6 py-2 bg-[#166534] text-white rounded-full font-semibold hover:bg-[#14532d] disabled:opacity-50 flex items-center gap-2"
                >
                  {photoSaving ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" strokeWidth={3} />
                      Guardar gasto con foto
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Photo preview lightbox */}
      {viewPhotoUrl && (
        <div
          data-testid="photo-lightbox"
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setViewPhotoUrl(null)}
        >
          <button
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white"
            aria-label="Cerrar"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={viewPhotoUrl}
            alt="Comprobante"
            className="max-h-[90vh] max-w-[95vw] rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
